import React, { useState } from "react";
import { cn } from "../../lib/utils";
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import type {
  SidebarNavProps,
  DashboardShellNavItem,
  DashboardShellNavSection,
  DashboardShellFooterAction,
  DashboardShellBrand,
} from "./types";

export function SidebarNav({
  brand,
  sections,
  footerItems = [],
  footer,
  compact = false,
  isLoading = false,
  onToggleCompact,
  onItemSelect,
  className,
}: SidebarNavProps) {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    const traverse = (items: DashboardShellNavItem[]) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          const hasActiveChild = item.children.some(c => c.active || (c.children && c.children.some(cc => cc.active)));
          initial[item.id] = item.defaultExpanded !== false || hasActiveChild;
          traverse(item.children);
        }
      });
    };
    sections.forEach(s => traverse(s.items));
    return initial;
  });

  const toggleItem = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const renderSkeleton = () => (
    <div className="space-y-4 px-3">
      {[1, 2, 3].map((section) => (
        <div key={section} className="space-y-2">
          {!compact && <div className="h-3 w-16 bg-muted rounded animate-pulse mb-3" />}
          {[1, 2, 3].map((item) => (
            <div key={item} className={cn("flex items-center gap-3", compact ? "justify-center" : "")}>
              <div className="h-8 w-8 rounded-md bg-muted animate-pulse shrink-0" />
              {!compact && <div className="h-4 w-32 bg-muted rounded animate-pulse" />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const Tooltip = ({ label, show }: { label: string; show: boolean }) => {
    if (!show) return null;
    return (
      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 hidden group-hover:flex px-2 py-1 bg-foreground text-background text-xs font-medium rounded shadow-sm z-[100] whitespace-nowrap pointer-events-none">
        {label}
        <div className="absolute top-1/2 -left-1 -translate-y-1/2 border-[3px] border-transparent border-r-foreground" />
      </div>
    );
  };

  const renderFooterItem = (item: DashboardShellFooterAction) => {
    const iconContent = item.icon ? (
      item.icon
    ) : compact ? (
      <span className="text-sm font-bold uppercase w-full flex justify-center items-center">
        {item.label.charAt(0)}
      </span>
    ) : null;

    return (
      <button
        key={item.id}
        onClick={() => !item.disabled && onItemSelect?.(item.id)}
        disabled={item.disabled}
        className={cn(
          "flex items-center py-2 text-sm transition-all duration-200 relative group",
          compact ? "justify-center px-0 h-10 rounded-md mx-auto w-10" : "w-full gap-3 px-3 min-h-[32px] rounded-md",
          item.active
            ? "bg-primary/10 text-primary font-medium"
            : "text-muted-foreground font-medium hover:bg-muted hover:text-foreground",
          item.disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {iconContent && (
          <span className={cn(
            "flex-shrink-0 flex items-center justify-center",
            compact ? "w-10 h-10 rounded-lg bg-muted/50 group-hover:bg-muted text-muted-foreground group-hover:text-foreground transition-colors" : "w-4 h-4",
            compact && item.active ? "bg-primary/10 text-primary group-hover:bg-primary/20" : ""
          )}>
            {iconContent}
          </span>
        )}

        {!compact && <span className="truncate flex-1 text-left">{item.label}</span>}

        {!compact && item.badge && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-medium group-hover:border-foreground/20">
            {item.badge}
          </span>
        )}

        {compact && item.badge && (
          <span className="absolute top-0 right-0 w-2 h-2 bg-primary rounded-full border border-card" />
        )}
        
        <Tooltip label={item.label} show={compact} />
      </button>
    );
  };

  const renderFlyoutItem = (item: DashboardShellNavItem) => {
    return (
      <button
        key={item.id}
        onClick={() => !item.disabled && onItemSelect?.(item.id)}
        disabled={item.disabled}
        className={cn(
          "flex items-center w-full gap-3 px-3 py-2 text-sm transition-all duration-200 rounded-md",
          item.active
            ? "bg-primary/10 text-primary font-medium"
            : "text-muted-foreground font-medium hover:bg-muted hover:text-foreground",
          item.disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {item.icon && <span className="w-4 h-4 shrink-0">{item.icon}</span>}
        <span className="truncate flex-1 text-left">{item.label}</span>
        {item.badge && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-medium">
            {item.badge}
          </span>
        )}
      </button>
    );
  };

  const renderItem = (item: DashboardShellNavItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isLeaf = !hasChildren;
    const isExpanded = expandedItems[item.id] !== false;
    const isCollapsible = !compact && hasChildren && item.collapsible;

    const iconContent = item.icon ? (
      item.icon
    ) : compact ? (
      <span className="text-sm font-bold uppercase w-full flex justify-center items-center">
        {item.label.charAt(0)}
      </span>
    ) : null;

    const activeStyles = "bg-primary/5 text-primary font-medium relative before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-r-full before:bg-primary";
    const inactiveStyles = "text-muted-foreground font-medium hover:bg-muted hover:text-foreground";

    const itemContent = (
      <button
        onClick={() => {
          if (isCollapsible) {
            toggleItem(item.id);
          } else if ((isLeaf || compact) && !item.disabled) {
            // In compact mode, we select if it's a leaf OR if we don't have flyout logic yet.
            // But we'll wrap this button in a popover for parents in a moment.
            if (isLeaf) onItemSelect?.(item.id);
          }
        }}
        disabled={item.disabled}
        className={cn(
          "flex items-center py-1.5 text-sm transition-all duration-200 relative group",
          compact ? "justify-center px-0 h-10 rounded-md mx-auto w-10 mb-1" : "w-full gap-3 px-3 min-h-[32px] rounded-md",
          depth > 0 && !compact ? "pl-9" : "",
          !compact && isLeaf && item.active ? activeStyles : 
          !compact && isLeaf && !item.active ? inactiveStyles :
          !compact && !isLeaf ? "text-foreground font-semibold mt-1 mb-0.5 cursor-default" :
          compact && item.active ? "bg-primary/10 text-primary font-medium" :
          compact && !item.active ? "text-muted-foreground hover:bg-muted" : "",
          item.disabled && "opacity-50 cursor-not-allowed",
          !isLeaf && !compact && !isCollapsible && "hover:bg-transparent"
        )}
        style={!isLeaf && !compact && !isCollapsible ? { cursor: 'default' } : undefined}
      >
        {iconContent && (
          <span className={cn(
            "flex-shrink-0 flex items-center justify-center", 
            compact ? "w-10 h-10 rounded-lg bg-muted/50 group-hover:bg-muted text-muted-foreground group-hover:text-foreground transition-colors" : "w-4 h-4",
            compact && item.active ? "bg-primary/10 text-primary group-hover:bg-primary/20" : "",
            !compact && item.active ? "text-primary" : ""
          )}>
            {iconContent}
          </span>
        )}
        
        {!compact && <span className="truncate flex-1 text-left">{item.label}</span>}
        
        {!compact && item.badge && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-medium group-hover:border-foreground/20">
            {item.badge}
          </span>
        )}

        {!compact && isCollapsible && (
          <ChevronDown 
            className={cn(
              "ml-auto h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
              !isExpanded && "-rotate-90"
            )}
          />
        )}

        {compact && item.badge && (
          <span className="absolute top-0 right-0 w-2 h-2 bg-primary rounded-full border border-card" />
        )}

        {/* Only show tooltip if NOT a parent with a popover */}
        {compact && isLeaf && <Tooltip label={item.label} show={compact} />}
      </button>
    );

    // In compact mode, parents get a popover
    if (compact && hasChildren) {
      return (
        <Popover.Root key={item.id}>
          <Popover.Trigger asChild>
            {itemContent}
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              side="right"
              sideOffset={12}
              align="start"
              className="z-[200] w-56 p-1.5 bg-card border border-border rounded-xl shadow-xl animate-in fade-in zoom-in-95 duration-150"
            >
              <div className="px-3 py-2 border-b border-border mb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {item.label}
                </span>
              </div>
              <div className="flex flex-col space-y-0.5">
                {item.children!.map((child) => renderFlyoutItem(child))}
              </div>
              <Popover.Arrow className="fill-border" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      );
    }

    return (
      <div key={item.id} className={cn("w-full")}>
        {itemContent}
        
        {!compact && hasChildren && (
          <div className={cn(
            "flex flex-col space-y-0.5 overflow-hidden transition-all duration-200 ease-out",
            isCollapsible && !isExpanded ? "max-h-0 opacity-0" : "max-h-[2000px] opacity-100 mt-0.5"
          )}>
            {item.children!.map((child) => renderItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (section: DashboardShellNavSection, index: number) => {
    return (
      <div 
        key={section.id} 
        className={cn(
          "flex flex-col space-y-1 relative",
          index !== sections.length - 1 && "pb-4 mb-4"
        )}
      >
        {!compact && section.label && (
          <h3 className="px-3 text-[12px] font-bold text-muted-foreground uppercase tracking-wider mb-1">
            {section.label}
          </h3>
        )}
        <div className="flex flex-col space-y-0.5">
          {section.items.map((item) => renderItem(item, 0))}
        </div>
        {index !== sections.length - 1 && (
          <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
        )}
      </div>
    );
  };

  return (
    <nav
      className={cn(
        "flex flex-col h-full bg-card transition-all duration-200 ease-out z-10",
        compact ? "w-16" : "w-64",
        className
      )}
    >
      <div className={cn(
        "flex border-b border-border shrink-0",
        compact ? "flex-col items-center py-2 gap-2" : "items-center px-4 h-14 gap-3 justify-between"
      )}>
        <div className="min-w-0 flex-1">
          {React.isValidElement(brand) ? (
            compact ? (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
                {(brand as any).props?.children?.[0] || (brand as any).props?.children || 'B'}
              </div>
            ) : brand
          ) : (
            <div className={cn("flex items-center gap-3", compact && "justify-center")}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground overflow-hidden shadow-sm">
                {(brand as DashboardShellBrand).icon || (
                  <span className="text-xs font-bold uppercase">
                    {(brand as DashboardShellBrand).monogram || (brand as DashboardShellBrand).title?.charAt(0)}
                  </span>
                )}
              </div>
              {!compact && (
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-bold truncate text-foreground leading-tight">
                    {(brand as DashboardShellBrand).title}
                  </span>
                  {(brand as DashboardShellBrand).subtitle && (
                    <span className="text-[10px] text-muted-foreground truncate leading-tight">
                      {(brand as DashboardShellBrand).subtitle}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={cn(
        "flex-1 overflow-y-auto overflow-x-visible py-4 space-y-2",
        compact ? "px-2" : "px-2"
      )}>
        {isLoading ? renderSkeleton() : sections.map((section, idx) => renderSection(section, idx))}
      </div>

      {footerItems.length > 0 && !isLoading && (
        <div className={cn(
          "shrink-0 py-2 border-t border-border bg-card",
          compact ? "px-2 space-y-1" : "px-2 space-y-0.5"
        )}>
          {footerItems.map(item => renderFooterItem(item))}
        </div>
      )}

      {footer && !isLoading && (
        <div className={cn(
          "p-3 border-t border-border shrink-0 bg-card",
          compact ? "px-2 flex justify-center" : "px-3"
        )}>
          {footer}
        </div>
      )}

      {onToggleCompact && !isLoading && (
        <div className={cn(
          "shrink-0 p-2 border-t border-border bg-card",
          compact ? "flex justify-center" : "px-2"
        )}>
          <button
            onClick={onToggleCompact}
            className={cn(
              "flex items-center gap-3 w-full py-2 px-3 text-sm font-medium rounded-md transition-colors group",
              "text-muted-foreground hover:bg-muted hover:text-foreground",
              compact && "justify-center px-0 h-10 w-10 mx-auto"
            )}
            title={compact ? "Expand sidebar" : "Collapse sidebar"}
          >
            {compact ? <PanelLeftOpen className="h-4 w-4 group-hover:scale-110 transition-transform" /> : <PanelLeftClose className="h-4 w-4 group-hover:scale-110 transition-transform" />}
            {!compact && <span className="truncate">Collapse Sidebar</span>}
          </button>
        </div>
      )}
    </nav>
  );
}
